/**
 * PHARMAKON — « Types de produits » (refonte WP-67, M-23; D-30, A-77, A-109): the pure model of the
 * settings section and its drawer (no React, unit-tested).
 *
 *   lignesTypes      the grid rows for a search / « Afficher les types désactivés »
 *   gestesType       what can be done on a type, with the reason when it cannot
 *   deplacement      the whole list with one type moved (PUT /ordre sends the whole list)
 *   reglesHeritees   what an owner type takes from its base (A-77): fields, péremption, producer, margin
 *   valeursInitiales / erreursType / corpsCreation / corpsModification   the drawer form
 *
 * Refonte WP-68 (A-117) — the catalogue's consumers (S1 « Type d'achat », the fiche produit and S2, the
 * quick creation, Stock & lots, the purchase journals, the Produits list, /recherche-produits) exchange
 * ONE type KEY: a predefined type's enum value, an owner type's id — what a row reads as
 * `type_officine_id ?? type_produit` (A-109 (1) NULL rule), served by the server as `cle`:
 *   typesPredefinis   the seven predefined rows (until the catalogue is read; the server always has them)
 *   cleDuType / cleTypeProduit / cleTypeAchat   the key of a catalogue row / a product / a facture
 *   libelleCle / libelleTypeProduit / libelleTypeAchat / baseDeCle   what a key reads as
 *   optionsTypes / optionsFiltreTypes   a picker's / a filter's options (no hard-coded list)
 *   choixTypeProduit / choixTypeAchat   what a writer sends for a chosen key
 *
 * The rules come from the shared twin (`champsDuType`, `margeAdmise`, the seven types) and the S1 CONFIG
 * (péremption per type, A-03); the server decides and answers in words — the client only pre-checks.
 */
import { CHAMP_TYPE, TYPES_PRODUIT, champsDuType, margeAdmise, typePorte } from "@pharmakon/shared/constants/produit-type-champs.js";
import {
  LIBELLE_TYPE_ACHAT_MIXTE,
  PEREMPTION_MASQUEE_POUR,
  PEREMPTION_MODE,
  PEREMPTION_OPTIONNELLE_POUR,
  TYPE_ACHAT_MIXTE,
} from "@/config/achat-saisie.config.js";
import { TYPE_PRODUIT_LABELS } from "@/config/app.config.js";
import { LIBELLES_CHAMPS_TYPE, TYPES_CATALOGUE_MESSAGES } from "@/config/types-produit.config.js";
import { formatDecimal } from "@/lib/number-format.js";

/** Accent- and case-insensitive text (the server's label key rule). */
export function plier(texte) {
  return String(texte ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("fr");
}

/** The label of a base type. */
export const libelleBase = (base) => TYPE_PRODUIT_LABELS[base] ?? base ?? "—";

/** The base types an owner type may take, in the enum order. */
export function optionsBases() {
  return TYPES_PRODUIT.map((t) => ({ value: t, label: libelleBase(t) }));
}

/**
 * The rows the grid shows: the search matches the label or the base (accents folded); deactivated types
 * only when asked. The order is the catalogue's.
 * @returns {{ lignes: object[], visibles: number, ajoutes: number }}
 */
export function lignesTypes(types, { recherche = "", inactifs = false } = {}) {
  const q = plier(recherche);
  const lignes = (types ?? []).filter((t) => {
    if (!inactifs && !t.actif) return false;
    if (!q) return true;
    return plier(t.libelle).includes(q) || plier(t.type_base_libelle ?? libelleBase(t.type_base)).includes(q);
  });
  return { lignes, visibles: lignes.length, ajoutes: lignes.filter((t) => !t.predefini).length };
}

/**
 * The whole list (ids) with `id` moved by `pas` (−1 up, +1 down) — what PUT /ordre sends — or null when
 * it cannot move (first / last, unknown).
 */
export function deplacement(types, id, pas) {
  const ids = (types ?? []).map((t) => t.id);
  const i = ids.indexOf(id);
  const j = i + pas;
  if (i < 0 || j < 0 || j >= ids.length) return null;
  const out = [...ids];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

/**
 * The gestures on a type, each `{ id, disponible, raison }` (a gesture not offered is absent):
 * modifier, désactiver / réactiver and supprimer (owner types only — a predefined type answers why),
 * monter / descendre (in the whole list).
 */
export function gestesType(type, { types = [], configurer = true, raisons }) {
  if (!type || !configurer) return [];
  const R = raisons;
  const usage = type.usage?.total ?? 0;
  const i = types.findIndex((t) => t.id === type.id);
  const gestes = [{ id: "modifier", disponible: true, raison: null }];
  if (type.predefini) {
    gestes.push({ id: "desactiver", disponible: false, raison: R.predefiniEtat });
    gestes.push({ id: "supprimer", disponible: false, raison: R.predefiniSupprime });
  } else {
    gestes.push(type.actif ? { id: "desactiver", disponible: true, raison: null } : { id: "reactiver", disponible: true, raison: null });
    gestes.push({ id: "supprimer", disponible: usage === 0, raison: usage === 0 ? null : R.utilise(type.usage) });
  }
  gestes.push({ id: "monter", disponible: i > 0, raison: i > 0 ? null : R.premier });
  gestes.push({ id: "descendre", disponible: i >= 0 && i < types.length - 1, raison: i >= 0 && i < types.length - 1 ? null : R.dernier });
  return gestes;
}

/** A-03 — the péremption of a base type, as S1 applies it. */
export function peremptionDeBase(base) {
  if (PEREMPTION_MASQUEE_POUR.includes(base)) return PEREMPTION_MODE.MASQUEE;
  if (PEREMPTION_OPTIONNELLE_POUR.includes(base)) return PEREMPTION_MODE.OPTIONNELLE;
  return PEREMPTION_MODE.REQUISE;
}

/**
 * A-77 — what a type of `base` takes from it: the type-dependent fields of the fiche produit, the
 * péremption rule, the producer field (laboratoire or marque) and whether a margin applies.
 */
export function reglesHeritees(base) {
  if (!base) return null;
  const champs = champsDuType(base);
  return {
    champs: champs.map((c) => LIBELLES_CHAMPS_TYPE[c] ?? c),
    peremption: peremptionDeBase(base),
    producteur: typePorte(base, CHAMP_TYPE.LABORATOIRE) ? "laboratoire" : typePorte(base, CHAMP_TYPE.MARQUE) ? "marque" : null,
    marge: margeAdmise(base),
  };
}

/** The drawer's starting values (a new type: no base chosen yet — the owner picks it). */
export function valeursInitiales(type = null) {
  if (!type) return { libelle: "", type_base: "", marge_pct: null };
  return { libelle: type.libelle, type_base: type.type_base, marge_pct: type.marge_pct ?? null };
}

const normalise = (l) => String(l ?? "").trim().replace(/\s+/g, " ");

/**
 * The pre-checks of the drawer (the server has the last word): label bounds, a base chosen, the margin
 * within the bounds and only where the base takes one.
 * @param {{ libelle: string, type_base: string, marge_pct: string|null }} v
 * @param {{ bornes: object, messages: object, margeTexteInvalide?: boolean }} opts
 */
export function erreursType(v, { bornes, messages, margeTexteInvalide = false }) {
  const D = messages;
  const e = {};
  const l = normalise(v.libelle);
  if (l.length < bornes.libelle_min) e.libelle = D.libelleRequis(bornes.libelle_min);
  else if (l.length > bornes.libelle_max) e.libelle = D.libelleTropLong(bornes.libelle_max);
  if (!v.type_base) e.type_base = D.baseRequise;
  const bornesMarge = D.margeBornes(String(bornes.marge_pct_min).replace(".", ","), String(bornes.marge_pct_max).replace(".", ","));
  if (margeTexteInvalide) e.marge_pct = bornesMarge;
  else if (v.marge_pct != null && v.marge_pct !== "") {
    if (v.type_base && !margeAdmise(v.type_base)) e.marge_pct = D.margeSansObjet;
    const n = Number(v.marge_pct);
    if (!Number.isFinite(n) || n < bornes.marge_pct_min || n > bornes.marge_pct_max) e.marge_pct = bornesMarge;
  }
  return e;
}

/** POST body of a new type. */
export function corpsCreation(v) {
  const corps = { libelle: normalise(v.libelle), type_base: v.type_base };
  if (v.marge_pct != null && v.marge_pct !== "" && margeAdmise(v.type_base)) corps.marge_pct = String(v.marge_pct);
  return corps;
}

const memeMarge = (a, b) => (a == null || a === "" ? null : Number(a)) === (b == null || b === "" ? null : Number(b));

/**
 * PATCH body: the changed fields only (a predefined type: its margin only). A re-based type whose new
 * base takes no margin sends `marge_pct: null` with it.
 */
export function corpsModification(v, type) {
  const corps = {};
  if (!type.predefini) {
    if (normalise(v.libelle) !== type.libelle) corps.libelle = normalise(v.libelle);
    if (v.type_base && v.type_base !== type.type_base) corps.type_base = v.type_base;
  }
  const base = corps.type_base ?? type.type_base;
  const marge = margeAdmise(base) ? (v.marge_pct === "" ? null : v.marge_pct ?? null) : null;
  if (!memeMarge(marge, type.marge_pct)) corps.marge_pct = marge == null ? null : String(marge);
  return corps;
}

/** The margin as the grid shows it (« 25,00 % »), the app's decimal format; null when none. */
export function texteMarge(marge) {
  if (marge == null || marge === "" || !Number.isFinite(Number(marge))) return null;
  return `${formatDecimal(marge, { decimals: 2 })} %`;
}

// ─── WP-68 (A-117) — the type key of the pickers, filters and facets ─────────────────────────────

/** The seven predefined types as catalogue rows, in the enum order (keys = enum values). */
export function typesPredefinis() {
  return TYPES_PRODUIT.map((t, i) => ({
    id: null,
    cle: t,
    code: t,
    libelle: libelleBase(t),
    type_base: t,
    predefini: true,
    actif: true,
    ordre: (i + 1) * 10,
  }));
}

/** The key of a catalogue row (the server's `cle`). */
export const cleDuType = (t) => t?.cle ?? (t ? (t.predefini ? t.type_base : t.id) : null) ?? null;

/** The key of a product (`type_officine_id` / its resume, else `type_produit`). */
export const cleTypeProduit = (p) => p?.type_officine_id ?? p?.type_officine?.id ?? p?.type_produit ?? null;

/** The key of a facture / BC (`type_achat_officine_id` / its resume, else `type_achat`; MIXTE has no row). */
export const cleTypeAchat = (d) => d?.type_achat_officine_id ?? d?.type_achat_officine?.id ?? d?.type_achat ?? null;

/** The catalogue rows by key. */
export function typeParCle(types) {
  return new Map((types ?? []).map((t) => [cleDuType(t), t]));
}

/**
 * What a key reads as: its catalogue label, « Mixte », the resume a row carries (an owner type the
 * catalogue does not list yet), the enum label — never a raw id.
 */
export function libelleCle(cle, types, { resume = null } = {}) {
  if (!cle) return "";
  if (cle === TYPE_ACHAT_MIXTE) return LIBELLE_TYPE_ACHAT_MIXTE;
  const t = typeParCle(types).get(cle);
  if (t) return t.libelle;
  if (resume?.libelle) return resume.libelle;
  return TYPE_PRODUIT_LABELS[cle] ?? TYPES_CATALOGUE_MESSAGES.typeInconnu;
}
export const libelleTypeProduit = (p, types) => libelleCle(cleTypeProduit(p), types, { resume: p?.type_officine });
export const libelleTypeAchat = (d, types) => libelleCle(cleTypeAchat(d), types, { resume: d?.type_achat_officine });

/** The base (behaviour key, A-77) of a key: its catalogue base, the enum value itself, null for MIXTE. */
export function baseDeCle(cle, types, { resume = null } = {}) {
  if (!cle || cle === TYPE_ACHAT_MIXTE) return null;
  const t = typeParCle(types).get(cle);
  if (t) return t.type_base;
  if (resume?.type_base) return resume.type_base;
  return TYPES_PRODUIT.includes(cle) ? cle : null;
}

const option = (t, { desactive = false } = {}) => ({
  value: cleDuType(t),
  label: desactive ? TYPES_CATALOGUE_MESSAGES.desactive(t.libelle) : t.libelle,
  type_base: t.type_base,
  predefini: Boolean(t.predefini),
  actif: t.actif !== false,
});

/**
 * A picker's options: the active types in the catalogue order; the row's current type (`courante`) kept
 * when it was deactivated since (marked) or is not listed yet (from its `resume`); `base` restricts to the
 * types of one base (a product whose lots fix its base); `referenceFigee` to what a writer that keeps
 * the stored reference can reach; « Mixte » last for a type d'achat.
 */
export function optionsTypes(types, { courante = null, resume = null, base = null, mixte = false, referenceFigee = false } = {}) {
  let liste = (types ?? []).filter((t) => (base ? t.type_base === base : true));
  if (referenceFigee) {
    // A writer that cannot change the catalogue reference (the D-13 rectification, EXE-102): the
    // current type, and the predefined types a change of enum value reaches unambiguously — never an
    // owner type, never the predefined type of the current owner type's base (A-109 (3) would keep it).
    const actuelle = typeParCle(types).get(courante);
    const baseFigee = actuelle && !actuelle.predefini ? actuelle.type_base : resume?.type_base ?? null;
    liste = liste.filter((t) => cleDuType(t) === courante || (t.predefini && t.type_base !== baseFigee));
  }
  const out = liste.filter((t) => t.actif !== false).map((t) => option(t));
  if (courante && courante !== TYPE_ACHAT_MIXTE && !out.some((o) => o.value === courante)) {
    const t = typeParCle(types).get(courante);
    if (t) out.push(option(t, { desactive: t.actif === false }));
    else if (resume?.libelle) out.push(option({ ...resume, cle: courante, predefini: false }, { desactive: resume.actif === false }));
  }
  if (mixte) out.push({ value: TYPE_ACHAT_MIXTE, label: LIBELLE_TYPE_ACHAT_MIXTE, type_base: null, predefini: true, actif: true });
  return out;
}

/** A filter's options: every type (a deactivated one still names its history, marked), « Mixte » for a type d'achat. */
export function optionsFiltreTypes(types, { mixte = false } = {}) {
  const out = (types ?? []).map((t) => option(t, { desactive: t.actif === false }));
  if (mixte) out.push({ value: TYPE_ACHAT_MIXTE, label: LIBELLE_TYPE_ACHAT_MIXTE, type_base: null, predefini: true, actif: true });
  return out;
}

/** The base of each option (value → type_base), for the producer facets (A-113 (4)). */
export function basesDesOptions(options) {
  return Object.fromEntries((options ?? []).filter((o) => o.type_base).map((o) => [o.value, o.type_base]));
}

/**
 * What a product writer sends for a chosen key: the enum (the type's base) and the catalogue reference
 * (null for a predefined type — A-109 (1)); null for a key the catalogue does not hold.
 */
export function choixTypeProduit(cle, types) {
  const t = typeParCle(types).get(cle);
  if (!t) return TYPES_PRODUIT.includes(cle) ? { type_produit: cle, type_officine_id: null } : null;
  return { type_produit: t.type_base, type_officine_id: t.predefini ? null : t.id };
}

/**
 * The type a NEW product takes from a key (S2 opened from a facture): the key's type while it is
 * active; a deactivated owner type gives its base (a new choice of it is refused, A-109 (4)); an unknown
 * key, `defaut`.
 */
export function choixPourCreation(cle, types, defaut) {
  const t = typeParCle(types).get(cle);
  if (t && t.actif === false) return { type_produit: t.type_base, type_officine_id: null };
  return choixTypeProduit(cle, types) ?? { type_produit: defaut, type_officine_id: null };
}

/** Same for a facture header: MIXTE stays MIXTE with no reference. */
export function choixTypeAchat(cle, types) {
  if (cle === TYPE_ACHAT_MIXTE) return { type_achat: TYPE_ACHAT_MIXTE, type_achat_officine_id: null };
  const c = choixTypeProduit(cle, types);
  return c ? { type_achat: c.type_produit, type_achat_officine_id: c.type_officine_id } : null;
}
